import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { safeGetItem, safeSetItem, safeRemoveItem } from './safe-storage';

export interface User {
  id?: number;
  name: string;
  email: string;
  createdAt?: string;
}

export interface LoginCredentials {
  email: string;
  password?: string;
}

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface SignInResponse {
  token: string;
  user: User;
  expiresIn?: number;
  maxAge?: number;
}

export interface MfaChallengeResponse {
  requiresMfa: true;
  mfaToken: string;
}

export interface SignupData {
  name: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
}

/**
 * Response shape from `POST /api/auth/ai-fill`. Password is intentionally
 * NOT generated server-side — the user picks their own.
 */
export interface AiFillSignupResponse {
  name: string;
  email: string;
}

const STORED_USER_KEY = 'auth_user';

function loadStoredUser(): User | null {
  const token = safeGetItem('session', 'auth_token');
  const raw = safeGetItem('session', STORED_USER_KEY);
  if (!token || !raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    safeRemoveItem('session', STORED_USER_KEY);
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/users`;
  private currentUserSubject = new BehaviorSubject<User | null>(loadStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  /**
   * Password sign-in — POSTs to /api/auth/login on the backend, which validates
   * the password (bcrypt) and returns a JWT plus the user record. The JWT is
   * persisted in sessionStorage under `auth_token` so the auth interceptor can
   * attach it to subsequent requests.
   *
   * Throws when the backend reports MFA is required — the current UI doesn't
   * yet have the verify-2FA step wired in; callers should display the error.
   */
  signIn(credentials: SignInCredentials): Observable<User> {
    const url = `${environment.apiUrl}/auth/login`;
    return this.http.post<SignInResponse | MfaChallengeResponse>(url, credentials).pipe(
      map((response) => {
        if ('requiresMfa' in response) {
          throw new Error('MFA required — verify-2FA flow not yet implemented in this client');
        }
        safeSetItem('session', 'auth_token', response.token);
        return response.user;
      }),
    );
  }

  /**
   * Email-OTP login step 1 — asks the backend to email a 6-digit
   * sign-in code to the supplied address. The backend always responds
   * `{ delivered: true }` regardless of whether the address is
   * registered (no user-enumeration via this endpoint), so the
   * frontend has no need to surface a "no such user" error here.
   */
  requestOtp(email: string): Observable<{ delivered: boolean }> {
    const url = `${environment.apiUrl}/auth/request-login-otp`;
    return this.http.post<{ delivered: boolean }>(url, { email });
  }

  /**
   * Email-OTP login step 2 — exchanges the 6-digit code for a signed
   * session. Mirrors {@link signIn} on success: the JWT is persisted
   * in `sessionStorage` so the auth interceptor can attach it to
   * subsequent requests. A wrong/expired code surfaces as the standard
   * 401 from the HTTP layer.
   */
  verifyOtp(email: string, code: string): Observable<User> {
    const url = `${environment.apiUrl}/auth/verify-login-otp`;
    return this.http.post<SignInResponse>(url, { email, code }).pipe(
      map((response) => {
        safeSetItem('session', 'auth_token', response.token);
        return response.user;
      }),
    );
  }

  /**
   * Legacy email-only lookup — used by the OTP path that has already verified
   * the user out-of-band and just needs the user record. Do NOT use for
   * password-based sign-in; that's `signIn()`.
   */
  login(credentials: LoginCredentials): Observable<User> {
    return this.http.get<User>(
      `${this.apiUrl}/search/by-email?email=${encodeURIComponent(credentials.email)}`,
    );
  }

  /**
   * Signup — POSTs to /api/auth/register on the backend, which creates the
   * user (bcrypt-hashing the password server-side) and returns a JWT plus
   * the user record. The JWT is persisted in sessionStorage under
   * `auth_token` so the user is signed in immediately after signup.
   */
  signup(data: SignupData): Observable<User> {
    const url = `${environment.apiUrl}/auth/register`;
    return this.http.post<SignInResponse>(url, data).pipe(
      map((response) => {
        safeSetItem('session', 'auth_token', response.token);
        return response.user;
      }),
    );
  }

  /**
   * Ask the backend to convert a free-text description into `{ name, email }`
   * for the signup form. Mirrors `ContactService.aiFill`. Password is filled
   * in by the user — the AI never sees or generates it.
   */
  aiFillSignup(prompt: string): Observable<AiFillSignupResponse> {
    const url = `${environment.apiUrl}/auth/ai-fill`;
    return this.http.post<AiFillSignupResponse>(url, { prompt });
  }

  /**
   * Get all users
   */
  getAllUsers(): Observable<User[]> {
    return this.http.get<User[]>(this.apiUrl);
  }

  /**
   * Get user by ID
   */
  getUserById(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  /**
   * Set current user
   */
  setCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    safeSetItem('session', STORED_USER_KEY, JSON.stringify(user));
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Request a password-reset link. POSTs the email to /api/auth/forgot-password
   * which always returns a generic 200 ack regardless of whether the address
   * is registered — see the backend controller for the no-enumeration
   * rationale. The frontend therefore surfaces the same success message on
   * any non-network response.
   */
  requestPasswordReset(email: string): Observable<{ delivered: boolean }> {
    return this.http.post<{ delivered: boolean }>(`${environment.apiUrl}/auth/forgot-password`, {
      email,
    });
  }

  /**
   * Redeem a password-reset token. POSTs to /api/auth/reset-password with
   * the token (from the email link) and the new plaintext password. The
   * server re-validates the 12-character policy and re-hashes with bcrypt.
   */
  resetPassword(token: string, password: string): Observable<{ user: User }> {
    return this.http.post<{ user: User }>(`${environment.apiUrl}/auth/reset-password`, {
      token,
      password,
    });
  }

  /**
   * Logout — clears the in-memory user and the persisted JWT.
   */
  logout(): void {
    this.currentUserSubject.next(null);
    safeRemoveItem('session', 'auth_token');
    safeRemoveItem('session', STORED_USER_KEY);
  }
}
